/**
 * Hook to subscribe to user-level SSE events for real-time notifications
 *
 * Connects to /api/events and receives ALL events for the user across all sessions:
 * - session:created → New incoming delegations or scheduled tasks
 * - llm:thinking, llm:chunk, llm:response → Messages in any session
 * - approval:request → Tool confirmation requests
 * - session:title-updated → Session title changes
 *
 * This replaces polling with a single persistent SSE connection for real-time updates.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatStore } from '@/stores/chatStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { useNotificationStore } from '@/stores/notificationStore.js';
import { useTasksStore, type TaskStatus } from '@/stores/tasksStore.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { getApiUrl } from '@/lib/api-url.js';
import { eventBus } from '@/lib/events/index.js';
import { transformSSEIntermediateEvent } from '@/lib/event-types.js';

/**
 * Extract the base API URL from the full agent URL.
 * URL format: http://host:port/api/{username}/agents/{agentDbId}
 * Returns: http://host:port/api
 */
function getApiBase(url: string): string {
    // Match pattern: /api/{username}/agents/{agentDbId}
    const match = url.match(/^(https?:\/\/[^/]+\/api)\/[^/]+\/agents\/[^/]+\/?$/);
    if (match) {
        return match[1];
    }
    // Fallback: just /api/agents/{agentDbId}
    const simpleMatch = url.match(/^(https?:\/\/[^/]+\/api)\/agents\/[^/]+\/?$/);
    if (simpleMatch) {
        return simpleMatch[1];
    }
    // Default fallback - assume URL is already the base
    return url;
}

export function useUserEvents() {
    const { authenticatedFetch, accessToken } = useAuth();
    const addToast = useNotificationStore((state) => state.addToast);
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isConnectedRef = useRef(false);
    const baseUrl = getApiUrl(); // Full agent URL for session calls
    const apiBase = getApiBase(baseUrl); // API base for events endpoint

    useEffect(() => {
        const connect = async () => {
            if (!accessToken) {
                console.warn('[UserEvents] No auth token available, skipping connection');
                return;
            }

            // Close existing connection
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }

            console.log('[UserEvents] Connecting to /api/events...', {
                baseUrl,
                apiBase,
                eventsUrl: `${apiBase}/events`,
                hasToken: !!accessToken,
                tokenPreview: accessToken?.substring(0, 20) + '...'
            });

            // EventSource doesn't support custom headers, so we need to use fetch + SSE parsing
            // or pass token as query param (less secure but works with EventSource)
            // For now, let's use a simpler approach with fetch + manual SSE parsing
            try {
                const response = await fetch(`${apiBase}/events`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Accept': 'text/event-stream',
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[UserEvents] Connection failed:', {
                        status: response.status,
                        statusText: response.statusText,
                        body: errorText
                    });
                    // Don't retry on 404 - endpoint doesn't exist (e.g., multi-agent-server)
                    if (response.status === 404) {
                        console.warn('[UserEvents] /events endpoint not available, disabling user events');
                        return; // Don't throw, just exit without scheduling reconnect
                    }
                    throw new Error(`SSE connection failed: ${response.status} - ${errorText}`);
                }

                if (!response.body) {
                    throw new Error('No response body');
                }

                isConnectedRef.current = true;
                console.log('[UserEvents] ✅ Connected to /api/events successfully');

                // Parse SSE stream
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let currentEventType = '';

                const readStream = async () => {
                    try {
                        while (true) {
                            const { done, value } = await reader.read();

                            if (done) {
                                console.log('[UserEvents] Stream closed by server');
                                break;
                            }

                            buffer += decoder.decode(value, { stream: true });

                            // Process complete SSE events
                            const lines = buffer.split('\n');
                            buffer = lines.pop() || ''; // Keep incomplete line in buffer

                            for (const line of lines) {
                                if (line.startsWith('event:')) {
                                    currentEventType = line.slice(6).trim();
                                } else if (line.startsWith('data:')) {
                                    const data = line.slice(5).trim();
                                    if (data) {
                                        handleSSEEvent(currentEventType, data);
                                    }
                                    currentEventType = ''; // Reset after processing data
                                } else if (line.startsWith(':')) {
                                    // Keepalive comment, ignore
                                } else if (line === '' && currentEventType) {
                                    // Empty line with event type but no data - reset
                                    currentEventType = '';
                                }
                            }
                        }
                    } catch (error) {
                        console.error('[UserEvents] Stream read error:', error);
                    } finally {
                        // Connection closed, attempt reconnect
                        isConnectedRef.current = false;
                        scheduleReconnect();
                    }
                };

                readStream();
            } catch (error) {
                console.error('[UserEvents] Connection error:', error);
                isConnectedRef.current = false;
                scheduleReconnect();
            }
        };

        const scheduleReconnect = () => {
            // Clear any existing reconnect timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            // Reconnect after 5 seconds
            reconnectTimeoutRef.current = setTimeout(() => {
                console.log('[UserEvents] Attempting reconnect...');
                connect();
            }, 5000);
        };

        const handleSSEEvent = (eventType: string, data: string) => {
            try {
                const parsed = JSON.parse(data);

                console.log('[UserEvents] 📥 Received SSE event:', {
                    eventType,
                    parsed,
                    sessionId: parsed.sessionId
                });

                // Handle connection event
                if (eventType === 'connected') {
                    console.log('[UserEvents] Connection confirmed');
                    return;
                }

                // Transform SSE event name to WebSocket format for compatibility
                // Note: Server sends 'agent:*' events (not 'llm:*') from ServerEventBus
                const eventMap: Record<string, string> = {
                    'session:created': 'session:created',
                    'session:title-updated': 'sessionTitleUpdated',
                    // Agent events from ServerEventBus (preferred)
                    'agent:thinking': 'thinking',
                    'agent:chunk': 'chunk',
                    'agent:response': 'response',
                    'agent:tool-call': 'toolCall',
                    'agent:tool-result': 'toolResult',
                    'agent:error': 'error',
                    'agent:run-complete': 'runComplete',
                    // Legacy llm:* events (for backwards compatibility)
                    'llm:thinking': 'thinking',
                    'llm:chunk': 'chunk',
                    'llm:response': 'response',
                    'llm:tool-call': 'toolCall',
                    'llm:tool-result': 'toolResult',
                    'llm:error': 'error',
                    'approval:request': 'approvalRequest',
                    'approval:response': 'approvalResolved',
                    'ui:action': 'uiAction',
                    'task:created': 'taskCreated',
                    'task:updated': 'taskUpdated',
                    'task:deleted': 'taskDeleted',
                    'externalAgentResponse': 'externalAgentResponse',
                    // Scheduler lifecycle events (execution events from backend)
                    'scheduler:triggered': 'scheduler:scheduleTriggered',
                    'scheduler:completed': 'scheduler:executionCompleted',
                    'scheduler:failed': 'scheduler:executionFailed',
                    // Agent run invoke - triggered when scheduler/external source starts execution
                    'agent:run-invoke': 'runInvoke',
                };

                const wsEventType = eventMap[eventType] || eventType;

                console.log('[UserEvents] Mapped to WS event type:', wsEventType);

                // If this is a thinking event, fetch messages to ensure we have the latest
                // including the incoming user message (since there's no event for it)
                if (eventType === 'llm:thinking' && parsed.sessionId) {
                    const currentSessionId = useSessionStore.getState().currentSessionId;
                    const messages = useChatStore.getState().getMessages(parsed.sessionId);

                    console.log('[UserEvents] Thinking event - checking messages:', {
                        sessionId: parsed.sessionId,
                        currentSessionId,
                        messageCount: messages.length,
                        isActiveSession: parsed.sessionId === currentSessionId
                    });

                    // Always fetch messages to ensure we have the latest incoming message
                    // This is necessary because there's no event emitted for incoming user messages
                    console.log('[UserEvents] Fetching messages to get latest incoming message...');
                    fetchMessagesForSession(parsed.sessionId);
                }

                // Create WebSocket-compatible event for existing transformers
                // Map SSE field names to WebSocket field names
                const wsEvent: any = {
                    type: wsEventType,
                    ...parsed,
                };

                // Map fields based on event type
                if ((eventType === 'agent:response' || eventType === 'llm:response') && parsed.content) {
                    // Map content → text for response events
                    wsEvent.text = parsed.content;
                    delete wsEvent.content;
                }

                if (eventType === 'agent:tool-call' || eventType === 'llm:tool-call') {
                    // Map args → toolArgs, callId → toolCallId
                    if (parsed.args) {
                        wsEvent.toolArgs = parsed.args;
                        delete wsEvent.args;
                    }
                    if (parsed.callId) {
                        wsEvent.toolCallId = parsed.callId;
                        delete wsEvent.callId;
                    }
                }

                if (eventType === 'agent:tool-result' || eventType === 'llm:tool-result') {
                    // Map callId → toolCallId, sanitized/rawResult → result
                    if (parsed.callId) {
                        wsEvent.toolCallId = parsed.callId;
                        delete wsEvent.callId;
                    }
                    // Use result (from server), sanitized, or rawResult (legacy)
                    if (parsed.result !== undefined) {
                        wsEvent.result = parsed.result;
                    } else if (parsed.sanitized !== undefined) {
                        wsEvent.result = parsed.sanitized;
                    } else if (parsed.rawResult !== undefined) {
                        wsEvent.result = parsed.rawResult;
                    } else {
                        // Ensure result is always defined (even if null) so UI knows processing is complete
                        wsEvent.result = null;
                    }
                    delete wsEvent.sanitized;
                    delete wsEvent.rawResult;
                    // Ensure success field is explicitly preserved (should already be from ...parsed, but be explicit)
                    wsEvent.success = parsed.success !== undefined ? parsed.success : false;
                }

                if ((eventType === 'agent:error' || eventType === 'llm:error') && parsed.error) {
                    // Map error object → error string
                    if (typeof parsed.error === 'object' && parsed.error.message) {
                        wsEvent.error = parsed.error.message;
                    }
                }

                // Handle scheduler tool results - update store from tool result data
                // Server sends 'agent:tool-result', keep 'llm:tool-result' for backwards compat
                if (eventType === 'agent:tool-result' || eventType === 'llm:tool-result') {
                    console.log('[UserEvents] 🔧 Tool result received:', {
                        eventType,
                        toolName: parsed.toolName,
                        callId: parsed.callId,
                        success: parsed.success,
                        hasRawResult: !!parsed.rawResult,
                        hasSanitized: !!parsed.sanitized,
                        hasResult: !!parsed.result,
                        allKeys: Object.keys(parsed),
                    });
                    if (parsed.toolName) {
                        handleSchedulerToolResult(parsed);
                    }
                }

                // Flatten scheduler event data - server sends { data: { schedule, ... } } but
                // the transform expects { schedule, ... } at top level
                if (eventType.startsWith('scheduler:') && parsed.data) {
                    wsEvent.schedule = parsed.data.schedule;
                    wsEvent.sessionId = parsed.data.sessionId;
                    wsEvent.triggeredAt = parsed.data.triggeredAt;
                    wsEvent.log = parsed.data.log;
                    wsEvent.error = parsed.data.error;
                }

                console.log('[UserEvents] Created WS event:', wsEvent);

                // Special handling for new sessions (incoming delegations)
                if (eventType === 'session:created') {
                    // Fetch the new session to add it to the list
                    handleNewSession(parsed.sessionId);
                    return;
                }

                // Special handling for task events (A2A async delegation updates)
                if (eventType === 'task:created' || eventType === 'task:updated' || eventType === 'task:deleted') {
                    handleTaskEvent(eventType, parsed);
                    // Continue to dispatch to event bus for any other listeners
                }

                // Special handling for external agent responses (A2A callback responses)
                if (eventType === 'externalAgentResponse') {
                    handleExternalAgentResponse(parsed);
                    // Continue to dispatch to event bus for any other listeners
                }

                // Special handling for scheduler events
                if (eventType.startsWith('scheduler:')) {
                    handleSchedulerEvent(eventType, parsed);
                    // Continue to dispatch to event bus for any other listeners
                }

                // Special handling for run-invoke (scheduled task / external trigger starting)
                // This activates streaming listeners and shows thinking indicator
                if (eventType === 'agent:run-invoke') {
                    handleRunInvoke(parsed);
                    // Continue to dispatch to event bus for other listeners
                }

                // Special handling for run-complete (scheduled task / external trigger finished)
                // This resets the tab status back to idle
                if (eventType === 'agent:run-complete') {
                    handleRunComplete(parsed);
                    // Continue to dispatch to event bus for other listeners
                }

                // Transform to AgentEvent and dispatch to event bus
                console.log('[UserEvents] Transforming and dispatching event...');
                const agentEvent = transformSSEIntermediateEvent(wsEvent);
                console.log('[UserEvents] Transformed event:', agentEvent);

                if (agentEvent) {
                    console.log('[UserEvents] ✅ Dispatching to event bus:', agentEvent.type);
                    eventBus.dispatch(agentEvent);
                } else {
                    console.warn('[UserEvents] ⚠️ Transform returned null/undefined');
                }
            } catch (error) {
                console.error('[UserEvents] Failed to parse event:', error, data);
            }
        };

        const fetchMessagesForSession = async (sessionId: string) => {
            try {
                console.log('[UserEvents] Fetching messages for session:', sessionId);
                const response = await authenticatedFetch(`${baseUrl}/sessions/${sessionId}/messages`);
                if (!response.ok) {
                    console.error('[UserEvents] Failed to fetch messages:', response.status);
                    return;
                }

                const messages = await response.json();
                console.log('[UserEvents] Loaded messages:', messages.length);

                // Set messages for this session
                useChatStore.getState().setMessages(sessionId, messages);
            } catch (error) {
                console.error('[UserEvents] Failed to fetch messages:', error);
            }
        };

        const handleNewSession = async (sessionId: string) => {
            try {
                // Fetch the new session details
                const response = await authenticatedFetch(`${baseUrl}/sessions`);
                if (!response.ok) return;

                const sessions = await response.json();
                const newSession = sessions.find((s: any) => s.id === sessionId);

                if (newSession) {
                    // Notify session store about the new session
                    useSessionStore.getState().notifySessionEvent({
                        type: 'created',
                        sessionId: newSession.id,
                        title: newSession.title,
                    });

                    // Fetch messages for the new session
                    await fetchMessagesForSession(sessionId);

                    // Show toast notification
                    addToast({
                        title: `📨 New message from ${newSession.title || 'Unknown'}`,
                        description: 'Click to open the conversation',
                        intent: 'success',
                        targetSessionId: newSession.id,
                    });

                    console.log('[UserEvents] Added new session with messages:', newSession.id);
                }
            } catch (error) {
                console.error('[UserEvents] Failed to fetch new session:', error);
            }
        };

        const handleRunInvoke = (data: any) => {
            try {
                console.log('[UserEvents] Run invoke (external trigger starting):', data);
                const { sessionId, source, content, metadata } = data;

                if (!sessionId) {
                    console.warn('[UserEvents] run:invoke missing sessionId');
                    return;
                }

                // Import stores for tab management
                const { addTab, getTab, updateTabStatus } = useChatTabsStore.getState();

                // Check if tab already exists for this session
                const tabId = `session-${sessionId}`;
                const existingTab = getTab(tabId);

                if (existingTab) {
                    // Tab exists - update its status to running
                    console.log('[UserEvents] Updating existing tab to running:', tabId);
                    updateTabStatus(tabId, 'running');
                } else {
                    // Create new tab with running status (don't make it active)
                    const title = source === 'scheduler' ? 'Scheduled Task' : 'Background Task';
                    console.log('[UserEvents] Creating new tab for triggered session:', tabId);
                    addTab({
                        id: tabId,
                        title,
                        sessionId,
                        isPinned: false,
                        status: 'running',
                        scheduleId: metadata?.scheduleId as string | undefined,
                    });
                }

                // Add the scheduler prompt as a user message so it's visible in the chat
                if (content) {
                    const chatStore = useChatStore.getState();
                    const now = Date.now();
                    const userMessage = {
                        id: `scheduler-${sessionId}-${now}`,
                        sessionId,
                        role: 'user' as const,
                        content: content,
                        timestamp: now,
                        createdAt: now,
                        metadata: {
                            source: source || 'scheduler',
                            isScheduledTask: true,
                        },
                    };
                    console.log('[UserEvents] Adding scheduler prompt as user message:', userMessage.id);
                    chatStore.addMessage(userMessage);
                }

                // Fetch messages for this session to ensure we have the latest
                // (This will also pick up any messages already in the session)
                console.log('[UserEvents] Fetching messages for triggered session:', sessionId);
                fetchMessagesForSession(sessionId);

                // Dispatch a thinking event to the event bus so the chat UI shows thinking indicator
                const thinkingEvent = {
                    type: 'thinking' as const,
                    id: `invoke-${sessionId}-${Date.now()}`,
                    sessionId,
                    messageId: `invoke-${sessionId}-${Date.now()}`,
                    timestamp: Date.now(),
                };

                console.log('[UserEvents] Dispatching thinking event for triggered session:', thinkingEvent);
                eventBus.dispatch(thinkingEvent);

                console.log('[UserEvents] ✅ Triggered session streaming activated:', sessionId);
            } catch (error) {
                console.error('[UserEvents] Failed to handle run:invoke:', error, data);
            }
        };

        const handleRunComplete = (data: any) => {
            try {
                console.log('[UserEvents] Run complete (external trigger finished):', data);
                const { sessionId } = data;

                if (!sessionId) {
                    console.warn('[UserEvents] run:complete missing sessionId');
                    return;
                }

                // Update tab status back to idle
                const { getTab, updateTabStatus } = useChatTabsStore.getState();
                const tabId = `session-${sessionId}`;
                const existingTab = getTab(tabId);

                if (existingTab) {
                    console.log('[UserEvents] Updating tab to idle:', tabId);
                    updateTabStatus(tabId, 'idle');
                }

                console.log('[UserEvents] ✅ Triggered session completed:', sessionId);
            } catch (error) {
                console.error('[UserEvents] Failed to handle run:complete:', error, data);
            }
        };

        const handleSchedulerToolResult = (data: any) => {
            try {
                // Server sends 'result' field, legacy sends 'rawResult' or 'sanitized'
                const { toolName, success, result: serverResult, rawResult, sanitized } = data;

                console.log('[UserEvents] 📅 handleSchedulerToolResult called:', {
                    toolName,
                    success,
                    hasResult: !!serverResult,
                    hasRawResult: !!rawResult,
                    hasSanitized: !!sanitized,
                });

                // Only handle scheduler tool results
                // Tool names: create_schedule, update_schedule, delete_schedule, list_schedules,
                //             get_schedule, get_schedule_history, trigger_schedule_now
                if (!toolName?.includes('schedule')) {
                    console.log('[UserEvents] Skipping - not a schedule tool:', toolName);
                    return;
                }

                // Skip failed tool calls
                if (!success) {
                    console.log('[UserEvents] Skipping - tool call failed');
                    return;
                }

                const schedulerStore = useSchedulerStore.getState();

                // Try result (from server), then rawResult, then sanitized (legacy)
                let result = serverResult || rawResult || sanitized;

                // Parse result if it's a string
                if (typeof result === 'string') {
                    try {
                        result = JSON.parse(result);
                        console.log('[UserEvents] Parsed string result:', result);
                    } catch (e) {
                        console.log('[UserEvents] Could not parse result as JSON:', result?.substring?.(0, 100));
                        return; // Can't parse, skip
                    }
                }

                if (!result) {
                    console.log('[UserEvents] No result to process');
                    return;
                }

                console.log('[UserEvents] 📅 Processing scheduler tool result:', toolName, result);

                // Extract base tool name (handles "custom--" prefix from dexto)
                const baseToolName = toolName.replace('custom--', '');

                switch (baseToolName) {
                    case 'create_schedule': {
                        const schedule = result.schedule;
                        if (schedule) {
                            schedulerStore.addSchedule(schedule);
                            addToast({
                                title: '📅 Routine created',
                                description: `"${schedule.name}" has been scheduled`,
                                intent: 'success',
                            });
                        }
                        break;
                    }

                    case 'update_schedule': {
                        const schedule = result.schedule;
                        if (schedule) {
                            schedulerStore.updateSchedule(schedule.id, schedule);
                        }
                        break;
                    }

                    case 'delete_schedule': {
                        const { scheduleId, scheduleName } = result;
                        if (scheduleId) {
                            schedulerStore.removeSchedule(scheduleId);
                            addToast({
                                title: '🗑️ Routine deleted',
                                description: `"${scheduleName || 'Routine'}" has been removed`,
                                intent: 'info',
                            });
                        }
                        break;
                    }

                    // Other scheduler tools (list, get, history, trigger) don't need store updates
                    default:
                        break;
                }
            } catch (error) {
                console.error('[UserEvents] Failed to handle scheduler tool result:', error, data);
            }
        };

        const handleExternalAgentResponse = (data: any) => {
            try {
                console.log('[UserEvents] External agent response:', data);
                const { taskId, content, sessionId } = data;

                if (!taskId) {
                    console.warn('[UserEvents] External agent response missing taskId');
                    return;
                }

                const tasksStore = useTasksStore.getState();
                const task = tasksStore.tasks.get(taskId);

                // Update task with response
                tasksStore.updateTask(taskId, {
                    status: 'completed',
                    response: {
                        text: content || '',
                    },
                    completedAt: new Date().toISOString(),
                });

                // Show toast notification
                if (task) {
                    addToast({
                        title: '✅ Response received',
                        description: `From ${task.remoteAgentName || task.remoteAgent}`,
                        intent: 'success',
                    });
                }

                // If there's a session, fetch messages to show the response in chat
                if (sessionId) {
                    fetchMessagesForSession(sessionId);
                }
            } catch (error) {
                console.error('[UserEvents] Failed to handle external agent response:', error, data);
            }
        };

        const handleTaskEvent = (eventType: string, data: any) => {
            try {
                const tasksStore = useTasksStore.getState();

                if (eventType === 'task:created') {
                    console.log('[UserEvents] Task created:', data);
                    const task = data.task;
                    if (!task) {
                        console.warn('[UserEvents] Task created event missing task data');
                        return;
                    }

                    // Determine direction based on whether we have a sessionId
                    const direction = task.sessionId ? 'outgoing' : 'incoming';

                    // Map API status to store status
                    let status: TaskStatus = task.status as TaskStatus;
                    if (direction === 'incoming' && task.status === 'submitted') {
                        status = 'pending_approval';
                    }

                    tasksStore.addTask({
                        taskId: task.taskId || data.taskId,
                        direction,
                        status,
                        remoteAgent: task.remoteAgent || 'Unknown',
                        remoteAgentName: task.remoteAgentName,
                        request: task.request || { text: 'Unknown request' },
                        response: task.response,
                        error: task.error,
                        sessionId: task.sessionId || data.sessionId,
                        createdAt: task.createdAt || new Date().toISOString(),
                        updatedAt: task.updatedAt || new Date().toISOString(),
                        completedAt: task.completedAt,
                    });

                    // Show toast for incoming tasks
                    if (direction === 'incoming') {
                        addToast({
                            title: '📥 New delegation request',
                            description: `From ${task.remoteAgentName || task.remoteAgent}`,
                            intent: 'info',
                        });
                    }
                } else if (eventType === 'task:updated') {
                    console.log('[UserEvents] Task updated:', data);
                    const task = data.task;
                    const taskId = task?.taskId || data.taskId;

                    if (!taskId) {
                        console.warn('[UserEvents] Task updated event missing taskId');
                        return;
                    }

                    // Build update object from task data
                    const updates: Partial<any> = {
                        status: task.status || data.status,
                        updatedAt: new Date().toISOString(),
                    };

                    if (task.response || data.response) {
                        updates.response = task.response || data.response;
                    }
                    if (task.error || data.error) {
                        updates.error = task.error || data.error;
                    }
                    if (task.completedAt) {
                        updates.completedAt = task.completedAt;
                    }
                    if (task.sessionId || data.sessionId) {
                        updates.sessionId = task.sessionId || data.sessionId;
                    }

                    tasksStore.updateTask(taskId, updates);

                    // Show toast for completed outgoing tasks
                    const existingTask = tasksStore.tasks.get(taskId);
                    if (existingTask?.direction === 'outgoing' && (task.status === 'completed' || data.status === 'completed')) {
                        addToast({
                            title: '✅ Delegation completed',
                            description: `Task from ${existingTask.remoteAgentName || existingTask.remoteAgent} finished`,
                            intent: 'success',
                        });
                    }
                } else if (eventType === 'task:deleted') {
                    console.log('[UserEvents] Task deleted:', data);
                    const taskId = data.taskId;
                    if (!taskId) {
                        console.warn('[UserEvents] Task deleted event missing taskId');
                        return;
                    }
                    tasksStore.removeTask(taskId);
                }
            } catch (error) {
                console.error('[UserEvents] Failed to handle task event:', error, data);
            }
        };

        const handleSchedulerEvent = (eventType: string, data: any) => {
            try {
                const schedulerStore = useSchedulerStore.getState();
                const action = eventType.replace('scheduler:', '');

                console.log('[UserEvents] Scheduler event:', action, data);

                // Note: CRUD events (created, updated, deleted) are no longer emitted.
                // Those are now handled via tool results in handleSchedulerToolResult.
                // This handler only processes execution lifecycle events.

                switch (action) {
                    case 'triggered': {
                        const schedule = data.schedule;
                        if (schedule) {
                            // Mark schedule as running
                            schedulerStore.setScheduleRunning(schedule.id, true);
                            addToast({
                                title: '▶️ Routine started',
                                description: `"${schedule.name}" is now running`,
                                intent: 'info',
                            });
                        }
                        break;
                    }

                    case 'completed': {
                        const { schedule, log } = data;
                        if (schedule) {
                            // Update schedule stats and mark as not running
                            schedulerStore.updateSchedule(schedule.id, schedule);
                            schedulerStore.setScheduleRunning(schedule.id, false);

                            // Add execution log if available
                            if (log) {
                                schedulerStore.addExecutionLog(log);
                            }

                            addToast({
                                title: '✅ Routine completed',
                                description: `"${schedule.name}" finished successfully`,
                                intent: 'success',
                            });
                        }
                        break;
                    }

                    case 'failed': {
                        const { schedule, error, log } = data;
                        if (schedule) {
                            // Update schedule stats and mark as not running
                            schedulerStore.updateSchedule(schedule.id, schedule);
                            schedulerStore.setScheduleRunning(schedule.id, false);

                            // Add execution log if available
                            if (log) {
                                schedulerStore.addExecutionLog(log);
                            }

                            addToast({
                                title: '❌ Routine failed',
                                description: `"${schedule.name}": ${error || 'Unknown error'}`,
                                intent: 'danger',
                            });
                        }
                        break;
                    }

                    default:
                        console.warn('[UserEvents] Unknown scheduler action:', action);
                }
            } catch (error) {
                console.error('[UserEvents] Failed to handle scheduler event:', error, data);
            }
        };

        // Start connection
        connect();

        // Cleanup on unmount
        return () => {
            console.log('[UserEvents] Cleaning up connection');
            isConnectedRef.current = false;

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [authenticatedFetch, baseUrl, accessToken, addToast]);
}
